import React from 'react'
import { QueryClientProvider } from 'react-query'
import { Router } from 'react-router-dom'
import zhTW from 'antd/lib/locale/zh_TW'
import { ConfigProvider } from 'antd'
import i18n from 'i18next'
import { RecoilRoot } from 'recoil'
import { ThemeProvider } from 'styled-components'
import '@assets/index.css'
import '@assets/locales/i18n'
import { baseTheme } from '@assets/theme'
import { Loading } from '@components/Loading'
import useAuth, { AuthContext, AuthProvider } from '@/auth'
import envStore from '@/env'
import useComapnyCode from '@/hooks/useComapnyCode'
import { history } from '@/routes'
import { createQueryClient } from '@/services'
import createPack from '@/utils/renderer/createPack'

type Props = {
  theme?: { [key: string]: any }
}

const ThemeProviderFC = React.memo<
  React.PropsWithChildren<{ theme?: Record<string, string> }>
>(props => {
  const auth = useAuth()
  const companyCode = useComapnyCode()

  const _defaultTheme = React.useMemo(() => {
    if (!companyCode) {
      return baseTheme.adminTheme
    }
    return baseTheme.normalTheme
    //eslint-disable-next-line
  }, [companyCode, auth])

  return (
    <ThemeProvider theme={_defaultTheme || {}}>{props.children}</ThemeProvider>
  )
})

const EnhancedApp = ({ theme = {}, children }: any) => {
  const [locale, setLocale] = React.useState<any>(zhTW)
  const [isFetchedRuntimeEnv, setFetchedRuntimeEnv] = React.useState(
    envStore.runTimeEnv.isFetchedRunTimeEnv
  )

  const Provider = createPack(
    <ConfigProvider locale={locale} csp={{ nonce: envStore.cspNonce }} />,
    <RecoilRoot />
  )

  React.useEffect(() => {
    const handelFetchedRunTimeEnv = () => {
      setFetchedRuntimeEnv(true)
    }
    envStore.runTimeEnv.addRunTimeEnvChangeListener(handelFetchedRunTimeEnv)
    return () => {
      envStore.runTimeEnv.removeRunTimeEnvChangeListener(
        handelFetchedRunTimeEnv
      )
    }
  }, [])

  React.useEffect(() => {
    i18n.on('languageChanged', async lang => {
      const localeModule = await import(`antd/lib/locale/${lang}.js`)

      setLocale(localeModule?.default)
    })
  }, [])

  return (
    <Provider>
      <AuthProvider>
        <ThemeProviderFC>
          <AuthContext.Consumer>
            {auth => (
              <QueryClientProvider client={createQueryClient(auth)}>
                {isFetchedRuntimeEnv ? (
                  <Router history={history}>{children}</Router>
                ) : (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: '#9090905b',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1,
                    }}>
                    <Loading />
                  </div>
                )}
              </QueryClientProvider>
            )}
          </AuthContext.Consumer>
        </ThemeProviderFC>
      </AuthProvider>
    </Provider>
  )
}

const renderer =
  (props: Props = {}) =>
  (App: React.FunctionComponent) => {
    return (
      <EnhancedApp {...props}>
        <App />
      </EnhancedApp>
    )
  }

export default renderer
